import React, {useMemo} from 'react'
import classNames from 'classnames'
import {getProgressBarBackground} from '@src/modules/Patrol/utils/getProgressBarBackground'
import {useAtalayaContext} from '@therms/atalaya'

interface RunProgressBarProps {
  isInProgress?: boolean
  showStatusLabel?: boolean
  visitsCount?: number
  visitsDoneCount?: number
}

function RunProgressBar({
  isInProgress,
  showStatusLabel,
  visitsCount = 0,
  visitsDoneCount = 0,
}: RunProgressBarProps) {
  const {isDarkMode, theme} = useAtalayaContext()
  const allVisitsCompleted = visitsDoneCount >= visitsCount

  const progressBarStyle = useMemo(() => {
    let color: string | undefined

    if (isInProgress) {
      color = isDarkMode ? theme.dark?.Main : theme.light?.Main
    } else if (!isInProgress && allVisitsCompleted) {
      color = isDarkMode ? theme.dark?.Positive : theme.light?.Positive
    }

    return getProgressBarBackground(
      Math.ceil((100 * visitsDoneCount) / visitsCount),
      color,
    )
  }, [isInProgress, allVisitsCompleted, visitsDoneCount, visitsCount])

  return (
    <div className="flex">
      <div className="bg-surface-subtle flex rounded-l text-xs font-bold tracking-tight">
        {showStatusLabel && (
          <span
            className={classNames(
              {
                'text-main': isInProgress,
                'text-neutral': !isInProgress && !allVisitsCompleted,
                'text-positive': !isInProgress && allVisitsCompleted,
              },
              'border-border px-xs py-xxs border-r',
            )}
          >
            {isInProgress && 'In Progress'}
            {!isInProgress && !allVisitsCompleted && 'Ended'}
            {!isInProgress && allVisitsCompleted && 'Completed'}
          </span>
        )}

        <span className="px-xs border-border py-xxs border-r font-mono">
          {visitsDoneCount}/{visitsCount}
        </span>
      </div>

      <div className="grow rounded-r" style={{background: progressBarStyle}} />
    </div>
  )
}

export {RunProgressBar}
